import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Eye } from 'lucide-react';
import DashboardLayout from '@/components/DashboardLayout';
import { API } from '@/App';
import { ProfileView } from '@/pages/satisfinder/SatisfinderDashboard';

const noop = () => {};

/** Read-only satisFINDER profile for Platform Admin + permitted staff. */
export default function SatisfinderAdminProfile() {
  const { satisfinderId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setData(null); setError(null);
    fetch(`${API}/admin/satisfinders/${satisfinderId}/profile`, { credentials: 'include' })
      .then(async (r) => {
        const d = await r.json().catch(() => ({}));
        if (r.ok) setData(d); else setError(d.detail || 'Could not load this profile.');
      })
      .catch(() => setError('Could not load this profile.'));
  }, [satisfinderId]);

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-4" data-testid="sf-profile-page">
        <Link to="/admin/satisfinders" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#1A3E6F] hover:underline" data-testid="sf-profile-back">
          <ArrowLeft className="w-4 h-4" /> Back to satisFINDERs
        </Link>
        {error && <div className="bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-lg p-4" data-testid="sf-profile-error">{error}</div>}
        {!data && !error && <div className="text-sm text-[#64748B] py-10 text-center" data-testid="sf-profile-loading">Loading…</div>}
        {data && (
          <>
            <ProfileHeader s={data.summary} photo={data.identity.profile_photo_url} />
            <div className="sfp-shell" style={{ minHeight: 'auto', background: 'transparent' }}>
              <ProfileView me={data} onSaved={noop} readOnly />
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

function displayName(s) {
  return [s.first_name, s.middle_name, s.last_name].filter(Boolean).join(' ') || s.full_name || 'satisFINDER';
}

function ProfileHeader({ s, photo }) {
  const name = displayName(s);
  return (
    <div className="bg-white rounded-xl border border-[#E2E8F0] p-5 flex flex-wrap items-center gap-5" data-testid="sf-profile-header">
      <div className="w-20 h-20 rounded-full bg-[#EFF6FF] text-[#1A3E6F] font-bold text-2xl flex items-center justify-center overflow-hidden shrink-0" data-testid="sf-profile-photo">
        {photo ? <img src={photo} alt={name} className="w-full h-full object-cover" /> : name.charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-[220px]">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="font-['Montserrat'] text-2xl font-bold text-[#0F172A]" data-testid="sf-profile-name">{name}</h1>
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border bg-[#EFF6FF] text-[#1A3E6F] border-[#BFDBFE]" data-testid="sf-profile-readonly-badge">
            <Eye className="w-3 h-3" /> View only
          </span>
        </div>
        <p className="text-sm text-[#64748B] mt-1" data-testid="sf-profile-contact">
          {[s.sf_public_id, s.email, s.mobile_number && `${s.country_code || ''} ${s.mobile_number}`.trim()].filter(Boolean).join(' · ')}
        </p>
        <div className="flex flex-wrap gap-2 mt-3" data-testid="sf-profile-statuses">
          <Chip label="Account" value={s.status} />
          <Chip label="Email" value={s.email_verified ? 'verified' : 'pending'} />
          <Chip label="KYC" value={s.kyc_status} />
          <Chip label="Payout" value={s.payout_status} />
          <Chip label="Joined" value={(s.created_at || '').slice(0, 10) || '—'} />
        </div>
      </div>
    </div>
  );
}

function Chip({ label, value }) {
  return (
    <span className="text-[11px] px-2 py-0.5 rounded-full border border-[#E2E8F0] bg-[#F8FAFC] text-[#334155]">
      <span className="font-semibold text-[#64748B]">{label}:</span> {String(value || '—').replace(/_/g, ' ')}
    </span>
  );
}
